import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  ChevronRight, 
  Footprints, 
  Timer, 
  UserCheck, 
  Map,
  AlertCircle,
  Eye,
  CheckCircle2
} from 'lucide-react';

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Solutions = () => {
  const solutions = [
    {
      title: "Footfall → Conversion",
      icon: Footprints,
      problem: "Retailers often guess why sales are down, unable to tell if traffic decreased or if the store failed to convert shoppers.",
      measures: "Walk-ins, passers-by, capture rate, and hourly store occupancy.",
      action: "Optimize staffing and marketing spend based on actual traffic peaks.",
      kpi: "+12-18% conversion",
      imageUrl: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=1200"
    },
    {
      title: "Queue & Wait Time",
      icon: Timer,
      problem: "Long queues lead to cart abandonment and poor brand perception, especially in QSR and high-volume retail.",
      measures: "Number of people in queue, average service time, and abandonment count.",
      action: "Trigger real-time alerts to open new registers or assist kitchen staff.",
      kpi: "-35% abandonment",
      imageUrl: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&q=80&w=1200"
    },
    {
      title: "Staff Productivity",
      icon: UserCheck,
      problem: "Inefficient scheduling leads to either overstaffing during lulls or understaffing during peaks.",
      measures: "Staff presence in zones, interaction frequency, and staff-to-customer ratios.",
      action: "Validate shift adherence and training effectiveness automatically.",
      kpi: "+20% efficiency",
      imageUrl: "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&q=80&w=1200"
    },
    {
      title: "Heatmaps & Layout",
      icon: Map,
      problem: "Merchandise layout is often based on aesthetic intuition rather than actual shopper engagement data.",
      measures: "Zone dwell time, aisle pathing, and interaction hot-spots.",
      action: "Identify 'dead zones' and prime locations for high-margin SKU placement.",
      kpi: "+15% cross-sell",
      imageUrl: "https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?auto=format&fit=crop&q=80&w=1200"
    }
  ];

  return (
    <div data-testid="solutions-page" className="bg-white">
      {/* Hero */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50"></div>
        <div className="container-custom relative z-10">
          <AnimatedSection>
            <div className="max-w-3xl">
              <h1 className="text-4xl lg:text-6xl font-extrabold font-heading text-foreground mb-6 leading-tight">
                Outcome-Driven <span className="text-gradient">Intelligence.</span>
              </h1>
              <p className="text-xl text-muted-foreground">
                We don't just provide data; we provide the clarity needed to make decisions that move the needle.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Solutions List */}
      <section className="py-12">
        <div className="container-custom">
          <div className="space-y-24">
            {solutions.map((sol, i) => (
              <AnimatedSection key={i}>
                <div 
                  data-testid={`solution-card-${i}`}
                  className={`flex flex-col lg:flex-row gap-12 items-center ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className="flex-1 space-y-5">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-2.5 rounded-xl bg-primary text-white">
                        <sol.icon size={22} />
                      </div>
                      <span className="px-3 py-1 rounded-full bg-secondary text-foreground text-xs font-bold uppercase tracking-wider border border-border">
                        Use Case 0{i+1}
                      </span>
                    </div>
                    <h2 className="text-3xl font-bold font-heading text-foreground">{sol.title}</h2>
                    
                    <div className="space-y-4">
                      <div className="flex gap-4 p-4 rounded-xl bg-secondary border border-border">
                        <AlertCircle size={20} className="text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">The Problem</h4>
                          <p className="text-foreground text-sm leading-relaxed">{sol.problem}</p>
                        </div>
                      </div>
                      
                      <div className="p-5 bg-white rounded-xl border-2 border-primary/20 shadow-sm">
                        <div className="flex gap-4">
                          <Eye size={20} className="text-primary shrink-0" />
                          <div>
                            <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-1">What ProAct AI Measures</h4>
                            <p className="text-foreground text-sm font-medium">{sol.measures}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 rounded-xl bg-secondary border border-border">
                        <CheckCircle2 size={20} className="text-green-500 mt-0.5 shrink-0" />
                        <div>
                          <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Action Enabled</h4>
                          <p className="text-foreground text-sm leading-relaxed">{sol.action}</p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 flex items-center gap-6">
                      <div className="text-4xl font-bold font-heading text-gradient">{sol.kpi}</div>
                      <div className="h-12 w-px bg-border hidden sm:block"></div>
                      <p className="text-xs text-muted-foreground uppercase font-semibold">Avg. deployment benchmark</p>
                    </div>
                  </div>
                  
                  <div className="flex-1 w-full relative">
                    <div className="absolute -inset-4 bg-gradient-to-tr from-primary/10 to-accent/10 rounded-3xl blur-2xl opacity-50"></div>
                    <div className="relative aspect-[16/10] rounded-2xl overflow-hidden shadow-2xl border border-border">
                      <img 
                        src={sol.imageUrl} 
                        className="w-full h-full object-cover" 
                        alt={sol.title} 
                      />
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-foreground text-white">
        <div className="container-custom text-center">
          <AnimatedSection>
            <h2 className="text-3xl font-bold font-heading mb-6">Ready to solve your operational challenges?</h2>
            <p className="text-white/70 mb-10 text-lg">Our team is ready to build a custom Vision AI plan for your business.</p>
            <Link 
              to="/contact" 
              data-testid="solutions-cta"
              className="bg-white text-foreground px-10 py-4 rounded-full font-bold text-lg inline-flex items-center hover:bg-primary hover:text-white transition-all"
            >
              Request Demo / Pilot <ChevronRight className="ml-2 w-5 h-5" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
